import { useFrame } from '@react-three/fiber';
import { usePathname } from 'next/navigation';
import { useRef } from 'react';
import { Camera, Group, Mesh, PerspectiveCamera, Vector3 } from 'three';

import CloudTransition from '0_canvas/CloudTransition/CloudTransition';
import EarthScene from '0_canvas/Scenes/EarthScene/EarthScene';
import useClientSideRouting from 'state/clientSideRoutingStore';
import {
  CUSTOM_CAMERA_VARIANT,
  useCustomCameraStore,
} from 'state/customCameraStore';

import * as A from './CustomCamera.animations';
import SETTINGS from './CustomCamera.settings';

const REAL_WORLD_CAMERA_POSITION = new Vector3();
const CAMERA_ROTATION_TEMPO = 0.0005;
const CAMERA_ROTATION_TEMPO_CONTRACTORS = 0.00025;
export const SPACE_VIEW_CAMERA_NEAR = 0.0001;

export interface CustomCameraOperator {
  radius: number;
  inclination: number;
  azimuth: number | false;
  near?: number;
}

const CustomCamera = () => {
  const savedView = useRef<CUSTOM_CAMERA_VARIANT>('SPACE');

  const pathname = usePathname();

  const operator = useRef<CustomCameraOperator>({
    radius: -1000,
    inclination: Math.PI / 4,
    azimuth: false,
    near: SPACE_VIEW_CAMERA_NEAR,
  });

  const azimuthGroup = useRef<Group>(null!);
  const inclinationGroup = useRef<Group>(null!);
  const cameraReference = useRef<Mesh>(null!);

  useFrame(({ camera }) => {
    if (handleWeatherException(camera)) return;

    runTransition();
    updateAzimuth();
    adjustScene(camera as PerspectiveCamera);
    setCameraNear(camera as PerspectiveCamera);
  });

  const handleWeatherException = (camera: Camera): boolean => {
    if (
      useClientSideRouting.getState().path !== 'weather' &&
      !pathname.includes('weather')
    )
      return false;

    camera.position.set(0, 0, -500);
    camera.rotation.set(-Math.PI, 0, -Math.PI);
    return true;
  };

  const adjustScene = (camera: PerspectiveCamera) => {
    if (!cameraReference.current) return;

    cameraReference.current.position.z = operator.current.radius;
    inclinationGroup.current.rotation.x = operator.current.inclination;
    azimuthGroup.current.rotation.y = operator.current.azimuth || 0;
    setCameraPosition(camera);
  };

  const setCameraPosition = (camera: PerspectiveCamera) => {
    cameraReference.current.getWorldPosition(REAL_WORLD_CAMERA_POSITION);
    camera.position.copy(REAL_WORLD_CAMERA_POSITION);
    camera.lookAt(0, 0, 0);
  };

  const setCameraNear = (camera: PerspectiveCamera) => {
    if (!operator.current.near) return;
    if (operator.current.near === camera.near) return;
    camera.near = operator.current.near;
    camera.updateProjectionMatrix();
  };

  const runTransition = () => {
    const view = useCustomCameraStore.getState().variant;

    const isIntro = savedView.current === 'SPACE' && view === 'TILES';
    const isOutro = savedView.current === 'TILES' && view === 'SPACE';

    if (savedView.current === view) return;
    savedView.current = view!;

    if (isIntro) return A.introAnimation(operator);
    if (isOutro) return A.outroAnimation(operator);
    A.animation(operator, view);
  };

  const updateAzimuth = () => {
    const settings = SETTINGS[savedView.current];
    if (!settings) return;

    const { azimuth } = settings;
    const { path } = useClientSideRouting.getState();

    operator.current.azimuth =
      azimuth === false
        ? (operator.current.azimuth || 0) +
          (path === 'contractors'
            ? CAMERA_ROTATION_TEMPO_CONTRACTORS
            : CAMERA_ROTATION_TEMPO)
        : azimuth;
  };

  return (
    <group>
      <group ref={azimuthGroup}>
        <group
          ref={inclinationGroup}
          rotation={[SETTINGS.SPACE!.inclination, 0, 0]}
        >
          <group position={[0, 0, -990]}>
            <EarthScene />
          </group>

          <group position={[0, 0, -1000]}>
            <CloudTransition />
          </group>

          <mesh
            ref={cameraReference}
            position={[0, 0, operator.current.radius]}
          >
            <boxGeometry args={[1, 1, 1]} />
            <meshBasicMaterial color={0x00ff00} />
          </mesh>
        </group>
      </group>
    </group>
  );
};

export default CustomCamera;
